/**
 * buildInterpretationPayload — deterministic InterpretationPayload (spec §7).
 */

import { DEFAULT_ORBS } from "./orbs.js";
import {
  deriveEvolutionaryPack,
  normalizeBodyName,
  toLonPoint,
} from "./derive.js";

/** Stable section order (spec §6). Core Overview (synthesis) leads. */
export const SECTION_PLAN = Object.freeze([
  "synthesis",
  "soulEngine",
  "bridge",
  "skippedSteps",
  "faces",
  "catalyst",
  "pastMastery",
  "saturnChiron",
  "higherOctave",
  "crucible",
  "moon",
  "sovereignty",
]);

const ASPECT_TYPES = new Set([
  "conjunction",
  "sextile",
  "square",
  "trine",
  "opposition",
  "semi-square",
  "sesquisquare",
]);

/**
 * @typedef {object} PayloadOptions
 * @property {string | null} [displayName]
 * @property {string | null} [placeLabel]
 * @property {Partial<typeof DEFAULT_ORBS>} [orbs]
 * @property {string} [computedAt] - ISO timestamp; defaults to now
 */

/**
 * @param {import("../celestine-adapter.js").ChartModel} chartModel
 * @param {PayloadOptions} [options]
 */
export function buildInterpretationPayload(chartModel, options = {}) {
  const cusps = chartModel.cusps || [];
  const pack = deriveEvolutionaryPack(chartModel, options.orbs || {});
  const { north, south } = pack._nodes;

  const angles = buildAngles(chartModel, cusps);
  const bodies = buildBodies(chartModel, cusps);
  const aspectsMajor = buildAspectsMajor(chartModel);

  const input = chartModel.input || {};

  /** @type {import("./derive.js").PayloadDerived} */
  const derived = {
    pluto: pack.pluto,
    nodalRulers: pack.nodalRulers,
    catalyst: pack.catalyst,
    pastMastery: pack.pastMastery,
    skippedSteps: pack.skippedSteps,
    angularHits: pack.angularHits,
    stelliums: pack.stelliums,
    saturn: pack.saturn,
    chiron: pack.chiron,
    uranus: pack.uranus,
    neptune: pack.neptune,
    moon: pack.moon,
    sovereigntyFlags: pack.sovereigntyFlags,
  };

  return {
    schemaVersion: 1,
    meta: {
      displayName: options.displayName ?? null,
      birth: {
        year: Number(input.year),
        month: Number(input.month),
        day: Number(input.day),
        hour: Number(input.hour),
        minute: Number(input.minute),
        second: Number(input.second ?? 0),
        timezoneHours: Number(input.timezone),
        latitude: Number(input.latitude),
        longitude: Number(input.longitude),
        placeLabel: options.placeLabel ?? null,
      },
      settings: {
        frame: "geocentric",
        zodiac: "tropical",
        houses: "placidus",
        nodes: "true",
      },
      computedAt: options.computedAt || new Date().toISOString(),
    },
    angles,
    bodies,
    nodes: {
      north: north,
      south: south,
    },
    aspectsMajor,
    derived,
    sectionPlan: [...SECTION_PLAN],
  };
}

/**
 * @param {import("../celestine-adapter.js").ChartModel} model
 * @param {object[]} cusps
 */
function buildAngles(model, cusps) {
  const map = new Map(
    (model.angles || []).map((a) => [normalizeBodyName(a.name), a])
  );
  const pick = (name) => {
    const row = map.get(name);
    if (!row) {
      throw new Error(`buildInterpretationPayload: missing angle ${name}`);
    }
    return toLonPoint({ ...row, kind: "angle" }, cusps);
  };
  return {
    asc: pick("Ascendant"),
    dsc: pick("Descendant"),
    mc: pick("Midheaven"),
    ic: pick("Imum Coeli"),
  };
}

/**
 * Planets + Chiron (nodes live under payload.nodes).
 * @param {import("../celestine-adapter.js").ChartModel} model
 * @param {object[]} cusps
 */
function buildBodies(model, cusps) {
  return (model.bodies || [])
    .filter((b) => {
      const n = normalizeBodyName(b.name);
      return n !== "North Node" && n !== "South Node";
    })
    .map((b) => toLonPoint(b, cusps));
}

/**
 * @param {import("../celestine-adapter.js").ChartModel} model
 */
function buildAspectsMajor(model) {
  const list = (model.aspects || [])
    .filter((a) => ASPECT_TYPES.has(String(a.type).toLowerCase()))
    .map((a) => ({
      body1: normalizeBodyName(a.body1),
      body2: normalizeBodyName(a.body2),
      type: capitalizeAspect(a.type),
      orb: Number(a.orb) || 0,
    }));

  list.sort((x, y) => x.orb - y.orb);
  return list;
}

/** @param {unknown} type */
function capitalizeAspect(type) {
  const s = String(type || "");
  const bare = s.includes(".") ? s.split(".").pop() : s;
  return bare.charAt(0).toUpperCase() + bare.slice(1).toLowerCase();
}
